"use client";

import * as React from "react";
import { Button } from "@workspace/ui/components/button";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";
import { StoryFilmDialog } from "../tech-stories/story-film-dialog";
import { homeTechStoriesSectionProps } from "../../_utils/tech-stories";
import { HeroBackgroundVideo } from "./hero-video/hero-background-video";
import { OvulationConfirmedCard } from "./hero-video/ovulation-confirmed-card";
import { LaunchOfferPill } from "./hero-video/launch-offer-pill";
import { StarRating } from "./hero-video/star-rating";

// Video hero for the region experiment: the monitor + strip transition plays once
// (muted, inline) and holds on its last frame; heading, rating, offer and CTA stay as
// live HTML on top. The page's fixed navbar sits over the top of this section.
// "Watch how Inito works" opens the same film as the tech stories section.

const HOW_INITO_WORKS_FILM = {
  ...homeTechStoriesSectionProps.film!,
  title: "How does Inito work?",
};

// Entrance animations, timed from the design mock-up and used on both layouts: the
// card fades in while rising (16px mobile / 9px desktop) over 0.6s ease-out, so it is
// visible right away; the offer pill follows 0.24s later with a 0.35s fade. Timing
// uses explicit properties because tw-animate-css's delay-*/duration-* utilities
// target animations.
const CARD_ENTER =
  "transition-[opacity,transform] ease-out [transition-duration:600ms] motion-reduce:transition-none";
const PILL_ENTER =
  "transition-opacity ease-linear [transition-delay:240ms] [transition-duration:350ms] motion-reduce:transition-none";

// Where the stage is narrower than the screen (tablets, monitors over 1920px), the sides
// continue the video's own edge colours: left half from its left edge, right half from
// its right edge, sampled top to bottom over the part of the frame the stage shows.
// The 1921px rule is important: Tailwind sorts it before `xl:`, whose gradient would win.
const STAGE_SIDE_FILL =
  "min-[601px]:max-xl:[background:linear-gradient(to_bottom,#6c6962_0%,#56554e_26%,#41403a_51%,#2b2926_77%,#141411_100%)_left/50%_100%_no-repeat,linear-gradient(to_bottom,#3b3934_0%,#302f2b_26%,#21201c_51%,#0f0d0e_77%,#000_100%)_right/50%_100%_no-repeat] " +
  "min-[1921px]:[background:linear-gradient(to_bottom,#51504a_0%,#474641_19%,#393833_47%,#2c2b27_75%,#1f1f1b_100%)_left/50%_100%_no-repeat,linear-gradient(to_bottom,#2e2c27_0%,#272621_19%,#1d1e19_47%,#11120d_75%,#080808_100%)_right/50%_100%_no-repeat]!";

// Figma's play-in-a-circle icon (22px mobile, 30px desktop: the same shape scaled).
function HeroPlayIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 22 22"
      fill="none"
      className={className}
    >
      <path
        d="M11 22C4.92487 22 0 17.0751 0 11C0 4.92487 4.92487 0 11 0C17.0751 0 22 4.92487 22 11C22 17.0751 17.0751 22 11 22ZM11 19.8C15.8601 19.8 19.8 15.8601 19.8 11C19.8 6.13989 15.8601 2.2 11 2.2C6.13989 2.2 2.2 6.13989 2.2 11C2.2 15.8601 6.13989 19.8 11 19.8ZM9.48409 7.05605L14.8509 10.6339C15.0531 10.7687 15.1077 11.0419 14.9729 11.2441C14.9406 11.2924 14.8992 11.3338 14.8509 11.3661L9.48409 14.9439C9.28191 15.0788 9.00867 15.0241 8.87392 14.8219C8.82574 14.7497 8.8 14.6648 8.8 14.5779V7.42215C8.8 7.17914 8.99701 6.98215 9.24 6.98215C9.3269 6.98215 9.41182 7.00786 9.48409 7.05605Z"
        fill="#000"
      />
    </svg>
  );
}

export function HeroSectionVideo() {
  const [isFilmOpen, setIsFilmOpen] = React.useState(false);
  const [isRevealed, setIsRevealed] = React.useState(false);
  const reveal = React.useCallback(() => setIsRevealed(true), []);
  const cardState = isRevealed
    ? "opacity-100 translate-y-0"
    : "opacity-0 translate-y-4 xl:translate-y-[9px]";
  const pillState = isRevealed ? "opacity-100" : "opacity-0";

  function handleCtaClick() {
    sendEvent(
      EventNames.HOW_INITO_WORK_CLICKED,
      withSectionType(SectionTypes.HERO_BANNER),
    );
    setIsFilmOpen(true);
  }

  return (
    <section
      id="hero-banner"
      data-testid="hero-banner"
      data-section-type="hero_banner"
      className={`relative w-full overflow-hidden bg-black xl:bg-[linear-gradient(170deg,#737068_0%,#000_100%)] ${STAGE_SIDE_FILL}`}
    >
      {/* Everything is laid out on this stage, in cqw (its width), so the composition
          keeps the Figma proportions. It stops growing at 600px (tablets get the phone
          layout, not a giant monitor) and 1920px (large monitors don't get an
          ever-taller hero); beyond that the section fills the sides. On laptops it is
          at least the screen height below the 48px top banner, so the hero fills the
          first view. */}
      <div className="relative mx-auto w-full max-w-[600px] [container-type:inline-size] xl:aspect-[100/55.7] xl:max-w-[1920px] xl:max-[1921px]:min-h-[calc(100svh-48px)]">
        <HeroBackgroundVideo onReveal={reveal} />

        {/* Card and (desktop) offer pill sit beside the monitor, positioned in cqw so
            they track the video at every width. */}
        <div className="pointer-events-none absolute inset-0 z-10">
          <OvulationConfirmedCard
            size="mobile"
            className={`absolute right-[3.95cqw] top-[16.05cqw] flex xl:hidden ${CARD_ENTER} ${cardState}`}
          />
          <OvulationConfirmedCard
            size="desktop"
            className={`absolute left-[71.9cqw] top-[10.35cqw] hidden xl:flex ${CARD_ENTER} ${cardState}`}
          />
          <LaunchOfferPill
            testId="hero-offer-pill-desktop"
            className={`absolute left-[49.9cqw] top-[40cqw] hidden bg-white/10 xl:inline-flex ${PILL_ENTER} ${pillState}`}
          />
        </div>

        <div className="relative px-8 pb-[53px] xl:flex xl:h-full xl:items-center xl:px-20 xl:pb-0 xl:pt-[90px]">
          {/* Mobile: content always starts below the monitor (Figma: offer at 396px of a 430px frame).
              Desktop: padded first, then capped at 1440px, like the logo / navbar row above,
              so the text lines up with the logo on wide screens. */}
          <div className="flex flex-col pt-[92.1cqw] xl:mx-auto xl:w-full xl:max-w-[1440px] xl:pt-0">
            <LaunchOfferPill
              testId="hero-offer-pill"
              className={`-ml-[3px] mb-[27px] inline-flex bg-black/30 xl:hidden ${PILL_ENTER} ${pillState}`}
            />

            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3 opacity-60 xl:gap-4">
                <StarRating className="h-[15.22px] w-[82px] xl:h-[15.6px] xl:w-[84px]" />
                <span
                  id="hero-trusted-by"
                  data-testid="hero-trusted-by"
                  className="captitle-12 xl:title-14 font-medium! text-[#faf6ef] xl:text-white"
                >
                  <span className="xl:hidden">TRUSTED BY 200K+ COUPLES</span>
                  <span className="hidden xl:inline">TRUSTED BY 200,000+ COUPLES</span>
                </span>
              </div>

              <div className="flex flex-col gap-5 xl:gap-6">
                <h1
                  id="hero-heading"
                  data-testid="hero-heading"
                  className="title-32 xl:title-48 text-white xl:max-w-[min(599px,41.6cqw)]"
                >
                  Track your fertility hormones at home with the most precise¹ readings
                </h1>

                <p
                  id="hero-description"
                  data-testid="hero-description"
                  className="body-18 max-w-[308px] text-white xl:max-w-[409px]"
                >
                  Measure Estrogen, LH, PdG (urine metabolite of progesterone) &amp;
                  FSH in just 10 minutes with Inito, the all-in-one fertility monitor.
                </p>
              </div>
            </div>

            <div className="mt-6 xl:mt-12">
              <Button
                id="hero-cta"
                data-testid="hero-cta"
                variant="white"
                size="default"
                className="h-12 w-fit gap-3 xl:h-14"
                onClick={handleCtaClick}
              >
                <HeroPlayIcon className="size-[22px] xl:size-[30px]" />
                Watch how Inito works
              </Button>
            </div>
          </div>
        </div>
      </div>

      <StoryFilmDialog
        film={HOW_INITO_WORKS_FILM}
        open={isFilmOpen}
        onOpenChange={setIsFilmOpen}
        sectionType={SectionTypes.HERO_BANNER}
        videoType="header_film"
      />
    </section>
  );
}
